import { TellescopeModelFromName, usePermissionQuery, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { Button, SearchInput } from "@tellescope/ui";
import { useFloatingScrollbar, useDebouncedValue } from "@tellescope/react-hooks";
import { Spinner } from "@tellescope/design-system";
import { Archive, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookingPageCard } from "./BookingPageCard";

export interface BookingPagesViewProps {
  getBookingPageOnClick: (
    bookingPage: TellescopeModelFromName<"appointment_booking_pages">
  ) => (() => void) | { to: string; replace?: boolean };
  getBookingPageOnDuplicate: (
    bookingPage: TellescopeModelFromName<"appointment_booking_pages">
  ) => (() => void) | { to: string; replace?: boolean };
  getBookingPageOnCreateNote: (
    bookingPage: TellescopeModelFromName<"appointment_booking_pages">
  ) => (() => void) | { to: string; replace?: boolean };
  onDelete: (bookingPage: TellescopeModelFromName<"appointment_booking_pages">) => void;
  onAddBookingPageClick: () => void;
  onCopyLink?: () => void;
  showArchived: boolean;
  toggleShowArchived: () => void;
}

export const BookingPagesView = ({
  getBookingPageOnClick,
  getBookingPageOnDuplicate,
  getBookingPageOnCreateNote,
  onDelete,
  onAddBookingPageClick,
  onCopyLink,
  showArchived,
  toggleShowArchived,
}: BookingPagesViewProps) => {
  const { t } = useTranslation("calendar");

  const [bookingPageSearchQuery, setBookingPageSearchQuery] = useState("");
  const [debouncedBookingPageSearch] = useDebouncedValue(bookingPageSearchQuery);

  const { scrollContainerRef, thumbRef, handleScroll } = useFloatingScrollbar({});

  const {
    create: { access: canCreate },
    update: { access: canUpdate },
  } = usePermissionQuery({
    model: "appointment_booking_pages",
    actions: ["create", "update"],
  });

  const bookingPagesQuery = useTellescopeInfiniteQuery({
    model: "appointment_booking_pages",
    method: "getSome",
    request: {
      search: debouncedBookingPageSearch
        ? {
            query: debouncedBookingPageSearch,
          }
        : undefined,
      limit: 100,
      mdbFilter: showArchived ? undefined : { archivedAt: { $in: [null, ""] } },
    },
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <section data-testid="booking-pages-page" className="shrink-0 p-6 border-b flex justify-between items-center">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold">{t("Booking Pages")}</h1>
          <h3 className="font-light text-muted-foreground" data-testid="booking-pages-count">
            {t("{{count}} booking pages", { count: bookingPagesQuery.data?.length ?? 0 })}
          </h3>
        </div>
        {canCreate && (
          <Button className="font-light" onClick={onAddBookingPageClick} data-testid="booking-page-add-button">
            <Plus />
            {t("Add booking page")}
          </Button>
        )}
      </section>
      <section className="overflow-y-hidden px-6 pt-6 @container flex flex-col">
        <div className=" pb-4 border-b shrink-0 flex justify-between items-center">
          <SearchInput
            wrapperClassName="w-[400px]"
            placeholder={t("Search booking pages...")}
            value={bookingPageSearchQuery}
            onChange={(e) => setBookingPageSearchQuery(e.target.value)}
            data-testid="booking-pages-search-input"
          />
          <Button
            variant={showArchived ? "default" : "outline"}
            className="border font-light"
            onClick={toggleShowArchived}
          >
            <Archive />
            {showArchived ? t("Hide Archived") : t("Show Archived")}
          </Button>
        </div>
        {bookingPagesQuery.isLoading ? (
          <div className="flex flex-col items-center gap-2 grow" data-testid="booking-pages-loading">
            <Spinner />
            <p className="text-muted-foreground text-sm">{t("Loading...")}</p>
          </div>
        ) : (
          <div className="relative grow min-h-0">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              data-testid="booking-pages-grid"
              className="grid grid-cols-1 @2xl:grid-cols-2 @4xl:grid-cols-3 @7xl:grid-cols-4 gap-4 py-4 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none" }}
            >
              {bookingPagesQuery.data?.map((bookingPage) => (
                <BookingPageCard
                  key={bookingPage.id}
                  bookingPage={bookingPage}
                  onClick={canUpdate ? getBookingPageOnClick(bookingPage) : undefined}
                  onDelete={() => onDelete(bookingPage)}
                  onDuplicate={getBookingPageOnDuplicate(bookingPage)}
                  onCopyLink={onCopyLink}
                  onCreateNote={getBookingPageOnCreateNote(bookingPage)}
                />
              ))}
            </div>
            <div
              ref={thumbRef}
              className="absolute top-0 right-1 w-1.5 bg-black/40 rounded-full pointer-events-none opacity-0 transition-opacity duration-300 z-[10000]"
            />
          </div>
        )}
      </section>
    </>
  );
};
